import type { PERIOD_TYPE } from 'constants/charting';

import type { DollarsString } from './dollars';
import type { MarketStatsType } from './historical_data';
import type { TradeStatus } from './trade';
import type { AccessLevel } from './user';

export * from './historical_data';
export * from './stats';
export * from './market';
export * from './incentive';

export type UserClearingType = 'kalshi' | 'self';

export interface UserVo {
    user_id: string;
    wire_code: string;
    created_ts: string;
    email: string;
    first_name: string;
    last_name: string;
    birth_date: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    deposits: number;
    use_bid_ask: boolean;
    country_code: string;
    area_code: string;
    phone_number: string;
    access_level: AccessLevel;
    frontend_sessions: number;
    perps_only: boolean;
    data_only?: boolean;
    is_institutional?: boolean;
    clearing_type?: UserClearingType;
    can_manage_sub_users?: boolean;
}

export interface LoadHistoryArgs {
    cached?: boolean;
    type?: MarketStatsType;
    period?: PERIOD_TYPE;
    acceptableAgeSeconds?: number;
    startTs?: number;
    candlestickFunction?: string;
}

export interface BarChartData {
    ts: number;
    volume: number;
    openInterest: number;
}

export interface PositionVo {
    user_id: string;
    market_id: string;
    market_ticker: string;
    position_fp: string; // # of contracts which have not been netted (negative means No, positive means Yes). 0 if market has settled.
    position_cost_dollars: DollarsString; // cost of current position, after netting, without fees. 0 if market has settled.
    total_cost_dollars: DollarsString; // cost paid opening a position or closing a position.
    buying_cost_dollars: DollarsString; // cost paid to open positions (not close them).
    realized_pnl_dollars: DollarsString; // value derived from netted & settled contracts
    fees_paid_dollars: DollarsString; // all trading fees paid
    // Exact per-leg fee breakdown (dollar strings, sub-cent precise). Used to
    // compute the paid-out / cost-with-fees figures without approximating the
    // split of `fees_paid` by contract volume. Optional so older payloads /
    // fixtures fall back to the proportional estimate.
    yes_buying_fee_cost_dollars?: DollarsString; // fees paid buying Yes contracts
    no_buying_fee_cost_dollars?: DollarsString; // fees paid buying No contracts
    position_fee_cost_dollars?: DollarsString; // fees attributable to the still-open position
    final_position_fee_cost_dollars?: DollarsString; // fees attributable to the settled final position
    volume_fp: string; // total number of contracts traded
    final_position_fp: string; // 0 if market not settled.
    final_position_cost_dollars: DollarsString; // 0 if market not settled.
    last_position_fp: string; // # of contracts sold before the market settled.
    is_settled: boolean; // true if the market is settled
    is_closed: boolean; // true if the market is closed
    // Market-scoped, unlike EventPositionVo.last_updated_ts (event-level).
    // Optional because the WS position channel's PositionDTO doesn't carry it.
    last_updated_ts?: string;
}

export interface EventPositionVo {
    user_id: string;
    event_ticker: string;
    collateral_returned_cents: number;
    last_updated_ts: string;
    market_positions: Array<PositionVo>;
}

export interface OrderBookEntryVo {
    price: number;
    count: number;
    is_yes: boolean;
}

export interface OrderBookEntryDollarsVo {
    price_dollars: DollarsString;
    count: number;
    is_yes: boolean;
}

export interface TradeVo {
    id: string;
    market_id: string;
    market_ticker: string;
    order_id: string;
    is_taker: boolean;
    status: TradeStatus;
    price: number;
    price_dollars?: DollarsString;
    count: number;
    count_fp?: string;
    create_date: string;
    is_yes: boolean;
    fee: number;
    fee_dollars?: DollarsString;
}

export interface TaxDocumentVo {
    name: string;
    url: string;
}

export interface AccountOperation {
    type: string;
    data: any;
    createdAt?: string;
}

export interface MarketTransaction {
    type: string;
    average_price_cents: number;
    fee_cents: number;
    is_side_yes: boolean;
    market_id: string;
    market_ticker: string;
    market_title: string;
    num_contracts: number;
    realized_cost_of_contracts_cents: number;
    realized_fifo_profit_cents: number;
    realized_revenue_cents: number;
    time: string;
}

export interface NotificationMessage {
    id: string;
    created_ts: string;
    user_id: string;
    type: string;
    is_delivered: boolean;
    is_read: boolean;

    content?: any;
    market_title?: string;
    event_ticker?: string;
    is_yes?: boolean;
    price?: number;
    contracts?: number;
    time?: string;
    partial?: boolean;
}

export interface PersonalizedSportsNotification {
    type: 'personalized_sports_notification';
    content: {
        events: {
            computed_title: string;
            series_ticker: string;
            event_ticker: string;
            event_title: string;
        }[];
        link: {
            event_ticker: string;
        };
    };
}

export type PriceMovementSubscriptionType = 'low_sensitivity' | 'high_sensitivity';

export interface PriceMovementSubscription {
    user_id: string;
    market_id: string;
    subscription_type: PriceMovementSubscriptionType;
}

export interface CustomPriceTarget {
    user_id: string;
    market_id: string;
    is_lower_bound: boolean;
    yes_price: number;
    is_presented_as_yes: boolean;
}

export interface RegulatorySection {
    name: string;
    slug: string;
    disclaimer?: string;
}

export interface RegulatoryDocument {
    title: string;
    tag: string;
    date: string;
    url: string;
}

export interface RegulatoryDocumentSummary {
    path: string;
    lastModified: string;
}

export interface RegulatoryDocumentSummaryPage {
    documents: RegulatoryDocumentSummary[];
    nextContinuationToken?: string;
}

export interface SocialCommentAndPostVo {
    comment: SocialCommentVo;
    post: SocialPostVo;
}

export interface SocialMediaAttachmentVo {
    provider: string;
    provider_id: string;
    url?: string;
    content_type?: string;
}

export interface SocialCommentVo {
    is_notable?: boolean;
    badge_image_url?: string | null;
    id: string;
    post_id: string;
    nickname: string;
    content: string;
    image_path: string;
    profile_image_path: string;
    created_ts: number;
    parent_comment_id?: string | null;
    depth?: number;
    likes_count?: number;
    liked?: boolean;
    is_deleted?: boolean;
    children?: SocialCommentVo[];
    media?: SocialMediaAttachmentVo[];
}

export interface SocialBadgeVo {
    badge_id: string;
    metric_name: string;
    category: string;
    time_period: string;
    user_rank: number;
    display_name: string;
    description: string;
}

export interface SocialPostSourceVo {
    source: string;
    title: string;
    url: string;
}

export interface SocialPostProductMetadataVo {
    sources: SocialPostSourceVo[];
    /** Settled market legs captured with an event-level ROI post. */
    event_strikes?: SocialPostEventStrikeVo[];
}

/** One settled market leg from an event-level social post snapshot. */
export interface SocialPostEventStrikeVo {
    market_ticker: string;
    side: 'yes' | 'no';
    /** Total buy-in for the leg, in centi-cents. */
    cost_centi_cents: number;
    /** Amount paid out for the leg, in centi-cents. */
    paid_out_centi_cents: number;
}

export interface SocialPostVo {
    is_notable?: boolean;
    badge_image_url?: string | null;
    id: string;
    market_title: string;
    event_title: string;
    event_long_title: string;
    series_title: string;
    market_ticker: string;
    event_ticker: string;
    series_ticker: string;
    series_tickers: string[];
    nickname: string;
    post_type: string;
    post_subtype: string;
    /** 'margin' marks a perps post; prediction-market posts omit it. */
    derivative_type?: string | null;
    /** Margin economics, serialized only on perps posts. */
    position_leverage?: string | number | null;
    initial_margin_cents?: string | number | null;
    position_notional_cents?: string | number | null;
    average_cost_cents?: string | number | null;
    entry_price_cents?: string | number | null;
    roi_range?: string | null;
    margin_snapshot_start_ts?: string | number | null;
    margin_snapshot_end_ts?: string | number | null;
    content: string;
    dollar_roi: number;
    percentage_roi: number;
    side: string;
    likes_count: number;
    comments_count: number;
    bookmarks_count: number;
    liked: boolean;
    bookmarked: boolean;
    created_ts: number;
    image_paths: string[];
    profile_image_path: string;
    product_metadata?: SocialPostProductMetadataVo;
    position_odds: number;
    cost_cents?: number;
    value_cents?: number;
    paid_out_cents?: number;
    payout_if_win_cents?: number;
    num_positions_on_event?: number;
    comments?: SocialCommentVo[];
    media?: SocialMediaAttachmentVo[];
}

export interface SocialLeaderboardPositionVo {
    nickname: string;
    value: number;
    is_anonymous: boolean;
    rank: number;
    profile_image_path?: string;
}

export interface SocialPreferencesVo {
    metric_signed_open_position: boolean;
    metric_open_interest: boolean;
    metric_volume: boolean;
    metric_dollars_traded: boolean;
    metric_dollars_investment: boolean;
    metric_pnl: boolean;
    metric_roi: boolean;
    metric_portfolio_value: boolean;
    metric_num_markets_traded: boolean;
    activity: boolean;
    /** Legacy preference; the current web API may omit it from responses. */
    top_holders?: boolean;
    portfolio: boolean;
    closed_portfolio: boolean;
    metric_category_positive_pnl: boolean;
    metric_category_pnl: boolean;
}

export interface SocialProfileMetricsVo {
    signed_open_position: number | null;
    open_interest: number | null;
    volume: number | null;
    dollars_traded: number | null;
    dollars_investment: number | null;
    pnl: number | null;
    portfolio_value: number | null;
    roi: number | null;
    num_markets_traded: number | null;
}

export interface SocialProfileCategoryMetricsVo {
    tag: string | null;
    value: number;
    category: string | null;
}

export interface SocialProfileHoldingsVo {
    event_ticker: string;
    series_ticker: string;
    total_absolute_position: number;
    market_holdings: SocialProfileHoldingVo[];
}

export interface SocialProfileHoldingVo {
    market_id: string;
    market_ticker: string;
    signed_open_position: number;
    pnl: number;
}

export interface SocialActivityItemVo {
    trade_id: string;
    market_id: string;
    ticker: string;
    price_dollars: string;
    count_fp: string;
    taker_side: string;
    taker_action: string;
    create_date: string;

    /** @deprecated Use price_dollars instead */
    price: number;
    /** @deprecated Use count_fp instead */
    count: number;
}

export type RFQRating = -1 | 0 | 1; // -1 is fair, 0 is good, 1 is excellent

export interface RFQVo {
    id: string;
    market_id: string;
    amount: number;
    price: number;
    is_yes: boolean;
    created_ts: number;
    expiration_ts: number;
    rating: RFQRating;
}

export interface NewRFQVo {
    id: string;
    creator_id: string;
    market_ticker: string;
    contracts?: number;
    status: string;
    created_ts: string;

    rest_remainder?: boolean;
    cancellation_reason?: string;
    creator_user_id?: string;
    cancelled_ts?: string;
    updated_ts?: string;
}

export interface NewQuoteVo {
    id: string;
    rfq_id: string;
    creator_id: string;
    rfq_creator_id: string;
    market_ticker: string;
    contracts: number;
    /** @deprecated Use yes_bid_dollars instead */
    yes_bid: number;
    /** @deprecated Use no_bid_dollars instead */
    no_bid: number;
    yes_bid_dollars?: DollarsString;
    no_bid_dollars?: DollarsString;
    created_ts: string;
    updated_ts: string;
    status: string;

    accepted_side?: string;
    accepted_ts?: string;
    confirmed_ts?: string;
    executed_ts?: string;
    cancelled_ts?: string;
    rfq_creator_order_id?: string;
    rfq_creator_user_id?: string;
    rest_remainder?: boolean;
    cancellation_reason?: string;
    creator_order_id?: string;
    creator_user_id?: string;
}

export interface SocialProfileVo {
    is_notable?: boolean;
    badge_image_url?: string | null;
    user_id: string;
    nickname: string;
    profile_image_path: string;
    description: string;
    pending_profile_image_path: string;
    joined_at?: string;
    follower_count?: number;
}

export interface SocialAccountNotificationSubscription {
    target_user_id: string;
    notify_on_posts: boolean;
    notify_on_trades: boolean;
    notify_on_new_positions: boolean;
    created_ts: string;
    updated_ts: string;
}

export type ParlayDesign = 'same_game_parlay' | 'multi_game_parlay';

export interface ParlaySelectionTab {
    event_tickers: string[];
    label: string;
}

export interface ParlaySelection {
    default_tab: number;
    label: string;
    tabs: ParlaySelectionTab[];
}

export interface AssociatedEvent {
    ticker: string;
    is_yes_only: boolean;
    size_max?: number;
    size_min?: number;
    active_quoters: string[];
}

export interface MultivariateEventCollectionVo {
    collection_ticker: string;
    series_ticker: string;
    title: string;
    description: string;
    functional_description: string;
    open_date: string;
    close_date: string;
    associated_events: AssociatedEvent[];
    is_ordered: boolean;
    product_metadata: any;
    size_max: number;
    size_min: number;
    navigation_hierarchy: Record<string, Record<string, string>>;
}

export interface ApiError {
    message: string;
    details: string;
    code: string;
    service: string;
    data: Record<string, unknown>;
}

export type ApiErrorType = {
    error: ApiError;
};

export interface ServerError {
    // This will be true if the browser didn't allow the request to be performed at all
    unknown: boolean;
    // This will be true in case the server fails with 5xx or doesn't respond at all to the request
    unavailable: boolean;
    internal: boolean;
    notFound: boolean;
    invalid: boolean;
    unauthorized: boolean;
    forbidden: boolean;
    conflict: boolean;
    maintenance: boolean;
    tooManyRequests: boolean;
    // This will be true in case the request timeouts in the front-end
    timeout: boolean;
    // This will be true in case the request is cancelled in the front-end
    cancelled: boolean;
    error: ApiError;
}
